import { getItemImage } from "@/utils/getItemImage";
import styled from "@emotion/styled";
import { type Item, type NormalizedDrawing, type Order } from "@repo/api";
import {
  Icon,
  ProductIdentifier,
  Text,
  Variables,
  boxIcon,
  chevronDownIcon,
  chevronUpIcon,
  imageStrikethroughIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  ClickableDiv,
  FlexDiv,
  InlineLabelField,
  Line,
  LocalizedDateAndTime,
  SimpleLabelField,
} from "@repo/ui";
import { hasItems, isLastItem, trackAnalytics } from "@repo/utils";
import { Fragment, useState } from "react";

import { Currency } from "./Currency";
import { MarginContainer } from "./layout/MarginContainer";

const ExpanderContainer = styled(ClickableDiv)`
  margin: 0 ${Variables.space50};
`;

const ImgPlaceholder = styled.div`
  width: 65px;
  height: 65px;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

interface ProductListItemProps {
  item?: Item;
  itemDetails?: any;
}

const getItemSalesPriceDetails = (item: Item) => {
  // group
  if (hasItems(item.childItems)) {
    const value = item.childItems.reduce(
      (prev, theSubItem) =>
        prev + (theSubItem.salesPrice?.value ?? 0) * (theSubItem.quantity ?? 1),
      0
    );
    return {
      value,
      currency: item.childItems[0].salesPrice?.currency,
    };

    // single item
  } else if (item.salesPrice) {
    return item.salesPrice;
  }

  return null;
};

const ProductInfoBox = ({ item, itemDetails }: ProductListItemProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  if (!item) {
    return null;
  }
  const salesPrice = getItemSalesPriceDetails(item);
  const imageUrl = getItemImage(item);

  return (
    <>
      <FlexDiv gap="24px">
        {imageUrl ? (
          <img src={imageUrl} alt={item.itemName} width="65px" height="65px" />
        ) : (
          <ImgPlaceholder>
            <Icon paths={imageStrikethroughIcon} />
            <Text>
              <T translationKey="prompt.no_image" />
            </Text>
          </ImgPlaceholder>
        )}
        <FlexDiv flexDirection="column" grow>
          <FlexDiv justifyContent="space-between">
            <Text textStyle="Heading.XS">{item.itemName}</Text>
            {salesPrice && (
              <FlexDiv>
                {item.childItems && item.childItems.length > 0 && (
                  <ExpanderContainer
                    aria-label="Expand"
                    onClick={() => {
                      setIsOpen(!isOpen);
                      trackAnalytics("open_expander");
                    }}
                  >
                    <Icon paths={isOpen ? chevronUpIcon : chevronDownIcon} />
                  </ExpanderContainer>
                )}
                <Text textStyle="Heading.XS">
                  <Currency
                    value={salesPrice.value}
                    currency={salesPrice.currency}
                  />
                </Text>
              </FlexDiv>
            )}
          </FlexDiv>
          <FlexDiv justifyContent="space-between">
            <div>
              {item.itemType !== "GRP" && item.itemType !== "GROUP" && (
                <ProductIdentifier value={item.itemNo} />
              )}
              {(item.itemType === "GRP" || item.itemType === "GROUP") && (
                <FlexDiv alignItems="center">
                  <MarginContainer>
                    <Icon paths={boxIcon} />
                  </MarginContainer>
                  <Text textStyle="Body.S">
                    {item.childItems?.length ?? 0}{" "}
                    <T translationKey="label.items" />
                  </Text>
                </FlexDiv>
              )}
            </div>
            <Text textStyle="Heading.XS">x {item.quantity ?? 1}</Text>
          </FlexDiv>
          {itemDetails && (
            <>
              {itemDetails.original_ordered_qty !== undefined && (
                <SimpleLabelField
                  small
                  label={t("label.original_ordered_qty")}
                  value={itemDetails.original_ordered_qty}
                />
              )}
              {itemDetails.total_reserved_qty !== undefined && (
                <SimpleLabelField
                  small
                  label={t("label.total_reserved_qty")}
                  value={itemDetails.total_reserved_qty}
                />
              )}
              {itemDetails?.expected_arrival_date && (
                <SimpleLabelField
                  small
                  label={t("label.expected_arrival_date")}
                  value={
                    <LocalizedDateAndTime
                      date={itemDetails.expected_arrival_date}
                    />
                  }
                />
              )}
            </>
          )}
        </FlexDiv>
      </FlexDiv>
      {isOpen && item.childItems && (
        <div id="product-child-list">
          {item.childItems.map((theSubItem) => (
            <ProductListItem item={theSubItem} key={theSubItem.itemNo} />
          ))}
        </div>
      )}
    </>
  );
};

const ProductListItem = ({ item, itemDetails }: ProductListItemProps) => {
  if (!item) {
    return null;
  }

  return <ProductInfoBox item={item} itemDetails={itemDetails} />;
};

interface AdditionalItemDetails {
  total_reserved_qty?: string;
  original_ordered_qty?: string;
  expected_arrival_date?: string;
}

interface ProductListProps {
  product?: Order | NormalizedDrawing;
  amount?: number;
  currency?: string;
  itemDetailsRecord?: Record<string, AdditionalItemDetails>;
}

export const ProductList = ({
  product,
  amount,
  currency,
  itemDetailsRecord,
}: ProductListProps) => {
  if (!product || !hasItems(product.items)) {
    return <Text>{t("prompt.not_available")}</Text>;
  }

  return (
    <div>
      {hasItems(product.items) && (
        <FlexDiv
          id="product-list"
          flexDirection="column"
          margin={`${Variables.space25} 0 ${Variables.space50}`}
          width="100%"
        >
          {product.items.map((item, i) => (
            <Fragment key={item.itemNo}>
              <ProductListItem
                key={item.itemNo}
                item={item}
                itemDetails={
                  itemDetailsRecord?.[`${item.itemType}${item.itemNo}`] ?? null
                }
              />
              {!isLastItem(product?.items, i) && <Line />}
            </Fragment>
          ))}
        </FlexDiv>
      )}

      <FlexDiv flexDirection="column">
        <Line />
        <FlexDiv margin={`${Variables.space100} 0`}>
          <InlineLabelField
            bold
            label={t("label.total")}
            value={
              amount != null && currency != null ? (
                <Currency value={amount} currency={currency} />
              ) : (
                t("prompt.not_available")
              )
            }
          />
        </FlexDiv>
      </FlexDiv>
    </div>
  );
};
